import React, { useEffect, useState } from 'react';
import './SplashScreen.css';

/**
 * MusicBox splash — plays on first load per session (24h TTL) or
 * skippable via tap. Respects prefers-reduced-motion.
 *
 * Sequence (~2.4s):
 *   0.0s  radial-mask reveal + logo scale/bounce start
 *   0.6s  ambient blobs breathe in behind
 *   0.9s  "MusicBox" wordmark rises + fades in
 *   1.2s  "BY MUSICBOOTH" attribution + shimmer starts
 *   1.3s  sparkles fly out
 *   2.0s  fade-out begins
 *   2.4s  splash unmounts, hands off to the app
 */
const STORAGE_KEY = 'mb_splash_seen_at';
const TTL_MS = 24 * 60 * 60 * 1000; // 24h

const shouldShowSplash = () => {
  try {
    if (typeof window === 'undefined') return false;
    // Respect reduced-motion — never show for those users.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return false;
    const last = Number(localStorage.getItem(STORAGE_KEY) || 0);
    if (Number.isFinite(last) && Date.now() - last < TTL_MS) return false;
    return true;
  } catch {
    return false;
  }
};

const markShown = () => {
  try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* noop */ }
};

export const SplashScreen = () => {
  const [visible, setVisible] = useState(() => shouldShowSplash());
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (!visible) return undefined;
    markShown();
    // Trigger fade at 2.0s
    const fadeT = setTimeout(() => setFading(true), 2000);
    // Unmount at 2.4s
    const doneT = setTimeout(() => setVisible(false), 2400);
    return () => { clearTimeout(fadeT); clearTimeout(doneT); };
  }, [visible]);

  if (!visible) return null;

  const skip = () => {
    setFading(true);
    setTimeout(() => setVisible(false), 200);
  };

  return (
    <div
      className={`mb-splash${fading ? ' fading' : ''}`}
      onClick={skip}
      role="button"
      tabIndex={-1}
      aria-label="MusicBox loading — tap to skip"
      data-testid="mb-splash"
    >
      <div className="mb-splash__blob mb-splash__blob--pink" />
      <div className="mb-splash__blob mb-splash__blob--green" />
      <div className="mb-splash__blob mb-splash__blob--purple" />

      <div className="mb-splash__stage">
        <div className="mb-splash__logo-wrap">
          <img
            src="/brand/mb-icon.png"
            alt="MB"
            className="mb-splash__logo"
            draggable={false}
          />
          {[...Array(8)].map((_, i) => (
            <span key={i} className={`mb-splash__sparkle mb-splash__sparkle--${i + 1}`} />
          ))}
        </div>
        <div className="mb-splash__wordmark">MusicBox</div>
        <div className="mb-splash__attr">
          by <span className="mb-splash__brand">MusicBooth</span>
        </div>
      </div>
    </div>
  );
};

export default SplashScreen;
